import { useId, useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { CaretDownIcon, PlusIcon } from '@phosphor-icons/react';
import { Checkbox } from '@/components/Checkbox';
import { Label } from '@/components/ui/Label';
import { Tag } from '@/components/Tag';
import { useModalPortalContainer } from '@/components/Modal/modalPortalContainer';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
    value: string;
    label: string;
}

interface MultiSelectProps {
    label?: string;
    value: string[];
    options: MultiSelectOption[];
    onChange: (value: string[]) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyMessage?: string;
    createLabel?: (value: string) => string;
    allowCreate?: boolean;
    disabled?: boolean;
    required?: boolean;
    className?: string;
}

function normalize(value: string): string {
    return value.trim().toLowerCase();
}

export function MultiSelect({
    label,
    value,
    options,
    onChange,
    placeholder = 'Select',
    searchPlaceholder = 'Search',
    emptyMessage = 'No options',
    createLabel = (input) => `Create "${input}"`,
    allowCreate = false,
    disabled = false,
    required = false,
    className,
}: MultiSelectProps) {
    const labelId = useId();
    const modalPortalContainer = useModalPortalContainer();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const optionByValue = useMemo(
        () => new Map(options.map((option) => [option.value, option])),
        [options],
    );
    const selected = value.map((item) => ({
        value: item,
        label: optionByValue.get(item)?.label ?? item,
    }));
    const selectedSet = new Set(value);
    const normalizedOptions = useMemo(
        () => new Set(options.map((option) => normalize(option.value))),
        [options],
    );
    const normalizedSelected = useMemo(
        () => new Set(value.map(normalize)),
        [value],
    );

    const filteredOptions = options.filter((option) => {
        const query = normalize(search);
        if (!query) return true;
        return (
            normalize(option.label).includes(query) ||
            normalize(option.value).includes(query)
        );
    });

    const createValue = search.trim();
    const canCreate =
        allowCreate &&
        createValue.length > 0 &&
        !normalizedOptions.has(normalize(createValue)) &&
        !normalizedSelected.has(normalize(createValue));

    const toggleValue = (nextValue: string) => {
        if (selectedSet.has(nextValue)) {
            onChange(value.filter((item) => item !== nextValue));
            return;
        }
        onChange([...value, nextValue]);
    };

    const createOption = () => {
        if (!canCreate) return;
        onChange([...value, createValue]);
        setSearch('');
    };

    return (
        <div className={cn('flex w-full flex-col gap-3', className)}>
            {label && (
                <Label
                    id={labelId}
                    required={required}
                    className={cn(
                        'w-fit text-sm-medium text-white-100',
                        disabled ? 'cursor-not-allowed' : 'cursor-default',
                    )}
                >
                    {label}
                </Label>
            )}
            <Popover.Root open={open} onOpenChange={setOpen}>
                <Popover.Trigger asChild>
                    <button
                        type="button"
                        aria-labelledby={label ? labelId : undefined}
                        disabled={disabled}
                        className="box-border flex min-h-[46px] w-full min-w-0 items-center justify-between gap-3 rounded-lg border border-grey-500 bg-transparent px-3 py-2 text-left outline-none transition-colors hover:border-grey-400 focus:border-grey-400 focus:ring-1 focus:ring-grey-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                            {selected.length > 0 ? (
                                selected.map((item) => (
                                    <Tag
                                        key={item.value}
                                        onRemove={() => toggleValue(item.value)}
                                    >
                                        {item.label}
                                    </Tag>
                                ))
                            ) : (
                                <span className="px-1 text-sm-medium text-grey-300">
                                    {placeholder}
                                </span>
                            )}
                        </span>
                        <CaretDownIcon className="size-4 shrink-0 text-grey-200" />
                    </button>
                </Popover.Trigger>
                <Popover.Portal container={modalPortalContainer}>
                    <Popover.Content
                        side="bottom"
                        align="start"
                        sideOffset={4}
                        className="z-50 w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-lg border border-grey-500 bg-grey-700 shadow-xl"
                        onOpenAutoFocus={(event) => event.preventDefault()}
                        onInteractOutside={() => setOpen(false)}
                    >
                        <div className="border-b border-grey-500 px-3 py-2">
                            <input
                                value={search}
                                onChange={(event) =>
                                    setSearch(event.target.value)
                                }
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        createOption();
                                    }
                                }}
                                placeholder={searchPlaceholder}
                                className="w-full bg-transparent text-sm text-white-100 placeholder:text-grey-400 outline-none"
                                autoFocus
                            />
                        </div>
                        <div className="max-h-56 overflow-y-auto p-2">
                            {filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => toggleValue(option.value)}
                                    className="flex min-h-8 w-full items-center gap-2 rounded-[5px] px-2 py-1.5 text-left text-sm text-grey-200 outline-none transition-colors hover:bg-grey-550 hover:text-white focus:bg-grey-550 focus:text-white"
                                >
                                    <Checkbox
                                        checked={selectedSet.has(option.value)}
                                    />
                                    <span className="min-w-0 flex-1 truncate">
                                        {option.label}
                                    </span>
                                </button>
                            ))}
                            {canCreate && (
                                <button
                                    type="button"
                                    onClick={createOption}
                                    className="flex min-h-8 w-full items-center gap-2 rounded-[5px] px-2 py-1.5 text-left text-sm text-grey-200 outline-none transition-colors hover:bg-grey-550 hover:text-white focus:bg-grey-550 focus:text-white"
                                >
                                    <PlusIcon className="size-4 shrink-0" />
                                    <span className="min-w-0 flex-1 truncate">
                                        {createLabel(createValue)}
                                    </span>
                                </button>
                            )}
                            {!filteredOptions.length && !canCreate && (
                                <div className="px-2 py-4 text-sm text-grey-300">
                                    {emptyMessage}
                                </div>
                            )}
                        </div>
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>
        </div>
    );
}
